import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useEffect } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_SPRING_CONFIG_INTRO } from '@experiences/blocs/utils/animations';
import { formatCurrency } from '@/utils/money';
import { LayoutChangeEvent, View } from 'react-native';
import { shadow } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';

// Hold the bar off-screen briefly after mount so it slides in after the
// screen settles, rather than racing the initial layout.
const ANIMATION_DELAY = 400;

interface QuoteSummaryBottomBarProps {
  /**
   * Customer name displayed on the bar. Callers should only render the
   * bar when an account is selected — passing an empty string leaves the
   * customer metric blank.
   */
  accountName: string;
  /** Currency the total and savings metrics render in (the quote's currency). */
  currencyCode: string;
  /** When true, a competitor is configured and the savings metric is shown. */
  isCompetitive: boolean;
  itemCount: number;
  onLayout?: (event: LayoutChangeEvent) => void;
  /** Subtotal across all line items, in micros. */
  totalAmountInMicros: number;
  /**
   * Competitor savings in micros. Positive when we're cheaper, negative when
   * we're more expensive. Rendered with sign under a "Savings" label.
   */
  totalSavingsInMicros: number | null;
}

/**
 * Pinned bottom summary bar for the Price Quotes editor. Reads pre-computed
 * totals from the parent (derived from `useQuoteState`) and renders customer,
 * item count, subtotal, and — when a competitor is configured — savings.
 *
 * Animates in with a delayed spring so the bar slides up after the screen
 * mounts.
 */
export const QuoteSummaryBottomBar: React.FC<QuoteSummaryBottomBarProps> = ({
  accountName,
  currencyCode,
  isCompetitive,
  itemCount,
  onLayout,
  totalAmountInMicros,
  totalSavingsInMicros,
}) => {
  const [styles, theme] = useStyles();

  const translateY = useSharedValue(100);

  // Intro animation: slide up from off-screen after a short delay.
  // useEffect is the standard reanimated pattern for "start on mount".
  useEffect(() => {
    translateY.value = withDelay(
      ANIMATION_DELAY,
      withSpring(0, {
        ...DEFAULT_SPRING_CONFIG_INTRO,
        velocity: 0,
      }),
    );
  }, [translateY]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Animated.View
      onLayout={onLayout}
      style={[styles.container, animatedStyle]}
    >
      <View style={styles.shadowContainer}>
        <View style={styles.content}>
          <View style={styles.metric}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-md"
            >
              {i18n.t('sales_portal.price_quotes.summary.customer')}
            </Typography>
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={1}
              variant="paragraph-md-600"
            >
              {accountName}
            </Typography>
          </View>

          <View style={styles.divider} />

          <View style={styles.metric}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-md"
            >
              {i18n.t('sales_portal.price_quotes.summary.items')}
            </Typography>
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-md-600"
            >
              {itemCount}
            </Typography>
          </View>

          <View style={styles.divider} />

          <View style={styles.metric}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-md"
            >
              {i18n.t('sales_portal.price_quotes.summary.total')}
            </Typography>
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-md-600"
            >
              {formatCurrency(totalAmountInMicros, currencyCode)}
            </Typography>
          </View>

          {isCompetitive && totalSavingsInMicros !== null ? (
            <>
              <View style={styles.divider} />
              <View style={styles.metric}>
                <Typography
                  color={theme.sk.color.text.secondary}
                  variant="paragraph-md"
                >
                  {i18n.t('sales_portal.price_quotes.summary.savings')}
                </Typography>
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-md-600"
                >
                  {formatCurrency(totalSavingsInMicros, currencyCode)}
                </Typography>
              </View>
            </>
          ) : null}
        </View>
      </View>
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    alignSelf: 'center',
    bottom: 0,
    position: 'absolute',
  },
  content: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
    paddingVertical: theme.sk.spacing.spaceSMedium,
    zIndex: 1,
  },
  divider: {
    backgroundColor: theme.sk.color.border.primary,
    width: 1,
  },
  metric: {
    flexDirection: 'column',
    flexShrink: 0,
  },
  shadowContainer: {
    borderRadius: theme.sk.radii.radiusLarge,
    marginBottom: theme.sk.spacing.spaceLarge,
    ...shadow({
      opacity: 0.12,
      radius: 24,
      x: 0,
      y: 12,
    }),
  },
}));
